import { convertLeadTimeByUnit } from '@/coreDomainsModule/leadtime';
import { DateService } from '@/coreDomainsModule/services/dateService';
import { UserAccountService } from '@/coreDomainsModule/services/userAccountService';
import { i18n } from '@/i18n';
import { AggregateBusinessNullableFormState, BaseBusinessNullableFormState, DEFAULT_WIDTH } from '../constants';
import { EditBusinessHeaderOption } from './useEditBusinessStore/types';
import { BusinessFreeColumnValue, IBusiness } from '@/coreDomainsModule/entities/businessBase';
import { FieldState } from '@/corePagesModule/composables/useForm';
import { UserAccount } from '@/coreDomainsModule/entities/users';

/**
 * 取引編集のViewModel
 */
export class EditBusinessViewModel {
  private t = i18n.t;
  /**
   * テーブル用の表示データを返す
   * @param key - ヘッダーキー
   * @param data - データ
   */
  public getDisplayData(key: string, data: IBusiness): string {
    if (key.startsWith('businessFreeColumnValues-')) {
      const columnNo = this.getBusinessFreeColumnNo(key);
      return data.businessFreeColumnValues?.find((column) => column.columnNo === columnNo)?.value || '--';
    }
    switch (key) {
      case 'package':
      case 'MOQ':
      case 'orderPackage':
      case 'purchaseUnitPrice':
      case 'salesUnitPrice':
        return data[key] ? Number(data[key])?.addComma() ?? '--' : '--';
      case 'leadTime':
        if (data['leadTime'] && data['leadTimeUnit'])
          return `${convertLeadTimeByUnit(data['leadTime'], data['leadTimeUnit'])}${this.t(data['leadTimeUnit'])}`;
        return '--';
      case 'stdInvIndex':
        if (!data.stdInventoryIndices) return '--';
        return (
          `${data.stdInventoryIndices['標準在庫指数'].min} ~ ` +
          `${data.stdInventoryIndices['標準在庫指数'].max}${i18n.locale === 'en' ? ' ' : ''}${this.t(
            data.stdInventoryIndices['標準在庫指数'].option.unit
          )}`
        );
      case 'aggregateBusinessResponsibleUser':
        return UserAccountService.getDisplayUsers(data.aggregateBusinessResponsibleUsers ?? []);
      case 'baseBusinessResponsibleUser':
        return UserAccountService.getDisplayUsers(data.baseBusinessResponsibleUsers ?? []);
      case 'createdAt':
      case 'updatedAt':
        return data[key] ? DateService.convertMSUnixTimeToYYYYMMDDhhmm(data[key]) : '--';
      default:
        return data[key] ?? '--';
    }
  }
  /**
   * ヘッダー情報からwidthを返す
   * @param header - ヘッダー情報
   * @param widthInfo - 保存された列幅情報
   */
  public getHeaderWidth(header: EditBusinessHeaderOption, widthInfo?: Record<string, number | undefined>): number {
    return widthInfo?.[header.key] ?? header.width ?? DEFAULT_WIDTH[header.type];
  }

  /**
   * データに変更があるかどうかをチェックする
   * @param headerKey - ヘッダーキー
   * @param data - データ
   * @param state - fieldState
   * @param nullableState - NULLにできる項目のState
   * @param businessFreeColumnValues - 一括編集で変更中の自由欄値
   */
  public hasEditedDiff(
    headerKey: string,
    data: IBusiness,
    state?: FieldState,
    nullableState?: AggregateBusinessNullableFormState | BaseBusinessNullableFormState,
    businessFreeColumnValues?: BusinessFreeColumnValue[]
  ): boolean {
    if (!state) return false;
    if (headerKey.startsWith('businessFreeColumnValues-')) {
      const columnNo = this.getBusinessFreeColumnNo(headerKey);
      const updatedValue = businessFreeColumnValues?.find((column) => column.columnNo === columnNo)?.value;
      if (updatedValue === undefined) return false;
      const currentValue = data.businessFreeColumnValues?.find((column) => column.columnNo === columnNo)?.value ?? '';
      return currentValue !== updatedValue;
    }
    const stdInvIndex = data.stdInventoryIndices?.['標準在庫指数'];
    switch (headerKey) {
      case 'warehouse':
        if (!state['warehouseName']?.value.value) return false;
        return data.warehouse !== state['warehouseName'].value.value;
      case 'package':
        if (!state[headerKey]?.value.value) return false;
        return Number(data[headerKey]) !== Number(state[headerKey]?.value.value);
      case 'leadTime':
        if (!state['leadTime']?.value.value && !state['leadTimeUnit']?.value.value) return false;
        if (!data.leadTime || !data.leadTimeUnit) return true;
        return (
          convertLeadTimeByUnit(data.leadTime, data.leadTimeUnit) !== Number(state['leadTime'].value.value) ||
          (!!state['leadTimeUnit'].value.value && data.leadTimeUnit !== this.convertDateUnit(state['leadTimeUnit'].value.value))
        );
      case 'stdInvIndex':
        if (
          !state['stdInventoryIndicesMax']?.value.value &&
          !state['stdInventoryIndicesMin']?.value.value &&
          !state['stdInventoryIndicesUnitNumb']?.value.value
        )
          return false;
        return (
          stdInvIndex?.max !== Number(state['stdInventoryIndicesMax'].value.value) ||
          stdInvIndex?.min !== Number(state['stdInventoryIndicesMin'].value.value) ||
          (!!state['stdInventoryIndicesUnit'].value.value &&
            stdInvIndex?.option.unit !== this.convertDateUnit(state['stdInventoryIndicesUnit'].value.value))
        );
      case 'aggregateBusinessResponsibleUser':
        if (nullableState?.[headerKey] && data.aggregateBusinessResponsibleUsers?.length) return true;
        if (!state['aggregateBusinessResponsibleUser']?.value.value) return false;
        return !this.isEqualSet(
          new Set(data.aggregateBusinessResponsibleUsers?.map((u) => u.email)),
          new Set(state['aggregateBusinessResponsibleUser'].value.value.split(','))
        );
      case 'baseBusinessResponsibleUser':
        if (nullableState?.[headerKey] && data.baseBusinessResponsibleUsers?.length) return true;
        if (!state['baseBusinessResponsibleUser']?.value.value) return false;
        return !this.isEqualSet(
          new Set(data.baseBusinessResponsibleUsers?.map((u) => u.email)),
          new Set(state['baseBusinessResponsibleUser'].value.value.split(','))
        );
      case 'unit':
      case 'userUniqueNumber':
      case 'manufacturer':
      case 'endUser':
        if (nullableState?.[headerKey] && data[headerKey]) return true;
        if (!state[headerKey]?.value.value) return false;
        return data[headerKey] !== state[headerKey]?.value.value;
      case 'MOQ':
      case 'orderPackage':
      case 'purchaseUnitPrice':
      case 'salesUnitPrice':
        if (nullableState?.[headerKey] && data[headerKey]) return true;
        if (!state[headerKey]?.value.value) return false;
        return Number(data[headerKey]) !== Number(state[headerKey]?.value.value);
      default:
        if (!state[headerKey]?.value.value) return false;
        return data[headerKey] !== state[headerKey]?.value.value;
    }
  }

  /**
   * テーブルに表示する差分情報の文字列を取得する
   * @param headerKey - ヘッダーキー
   * @param state - fieldState
   * @param nullableState - NULLにできる項目のState
   * @param selectedUserAccounts - 選択中のユーザーアカウント
   * @param businessFreeColumnValues - 一括編集で変更中の自由欄値
   */
  public getDisplayDiffData(
    headerKey: string,
    state?: FieldState,
    nullableState?: AggregateBusinessNullableFormState | BaseBusinessNullableFormState,
    selectedUserAccounts?: UserAccount[],
    businessFreeColumnValues?: BusinessFreeColumnValue[]
  ): string {
    if (!state) return '';
    let res = '';
    if (headerKey.startsWith('businessFreeColumnValues-')) {
      const columnNo = this.getBusinessFreeColumnNo(headerKey);
      res = businessFreeColumnValues?.find((column) => column.columnNo === columnNo)?.value ?? '';
      return ' → ' + (res || '--');
    }
    switch (headerKey) {
      case 'warehouse':
        res = state['warehouseName'].value.value;
        break;
      case 'leadTime':
        res = state['leadTime'].value.value + this.t(this.convertDateUnit(state['leadTimeUnit'].value.value));
        break;
      case 'stdInvIndex':
        res =
          state['stdInventoryIndicesMin'].value.value +
          ' ~ ' +
          state['stdInventoryIndicesMax'].value.value +
          this.t(this.convertDateUnit(state['stdInventoryIndicesUnit'].value.value));
        break;
      case 'aggregateBusinessResponsibleUser':
      case 'baseBusinessResponsibleUser':
        if (nullableState?.[headerKey]) {
          res = '--';
        } else {
          res = selectedUserAccounts?.map((u) => u.name || u.email).join(',') || '--';
        }
        break;
      case 'manufacturer':
      case 'endUser':
      case 'unit':
      case 'userUniqueNumber':
      case 'MOQ':
      case 'orderPackage':
      case 'purchaseUnitPrice':
      case 'salesUnitPrice':
        if (nullableState?.[headerKey]) {
          res = '--';
        } else {
          res = state[headerKey]?.value.value;
        }
        break;
      default:
        res = state[headerKey]?.value.value;
    }
    return ' → ' + res;
  }

  /**
   * リードタイムなどの単位をAPIに投げれる形に変換する
   * @param unit - 単位
   */
  private convertDateUnit(unit: string) {
    if (!unit) return '';
    switch (unit) {
      case this.t('日'):
        return 'day';
      case this.t('週'):
        return 'week';
      case this.t('月'):
        return 'month';
      default:
        return '';
    }
  }

  /**
   * SetのEqual比較
   * @param a - a
   * @param b - b
   */
  private isEqualSet<T>(a: Set<T>, b: Set<T>): boolean {
    return a.size === b.size && [...a].every((v) => b.has(v));
  }

  /**
   * 自由欄ヘッダーキーから自由欄番号を取得する
   * @param key - 自由欄ヘッダーキー
   */
  private getBusinessFreeColumnNo(key: string): number {
    return Number(key.replace('businessFreeColumnValues-', ''));
  }
}
